import { onPageScroll, pageScrollTop, prefersReducedMotion, session } from '@lucid/core';
import { lockScroll, unlockScroll } from '@theme/utilities';

const TONE_AFTER = 40;
const HIDE_THRESHOLD = 6;
const OPEN_DELAY = 70;
const CLOSE_DELAY = 220;
const LINK_CLOSE_DELAY = 120;
const SEARCH_MIN = 2;
const SEARCH_DEBOUNCE = 180;
const MOBILE_QUERY = '(max-width: 749px)';
const COMPACT_QUERY = '(max-width: 989px)';

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([type="hidden"]):not([disabled]), select, [tabindex]:not([tabindex="-1"])';
const CLICK_ONLY = new Set(['search', 'locale']);

/** Elements reached by Tab, in order: one stop per radio group (the checked radio, else the first). */
function tabStops(root) {
  const seen = new Set();
  const stops = [];
  for (const el of root.querySelectorAll(FOCUSABLE)) {
    if (!(el instanceof HTMLElement) || el.closest('[hidden], [inert]') || el.getClientRects().length === 0) continue;
    if (el instanceof HTMLInputElement && el.type === 'radio' && el.name) {
      const key = `${el.form?.id || ''}:${el.name}`;
      if (seen.has(key)) continue;
      seen.add(key);
      const group = [...root.querySelectorAll(`input[type="radio"][name="${CSS.escape(el.name)}"]`)].filter(
        (radio) => radio instanceof HTMLInputElement && !radio.disabled && !radio.closest('[hidden]')
      );
      stops.push(group.find((radio) => /** @type {HTMLInputElement} */ (radio).checked) || group[0] || el);
      continue;
    }
    stops.push(el);
  }
  return stops;
}

/** Moves deferred image sources into place, once. */
function loadImages(root) {
  if (!root) return;
  for (const img of root.querySelectorAll('img[data-src]')) {
    if (img.dataset.srcset) img.srcset = img.dataset.srcset;
    img.src = img.dataset.src;
    img.removeAttribute('data-src');
    img.removeAttribute('data-srcset');
  }
}

/** Live results for one search form: section-rendered predictive search, compact list. */
class HeaderSearch {
  /**
   * @param {HTMLFormElement} form
   * @param {LucidHeader} header
   */
  constructor(form, header) {
    this.form = form;
    this.header = header;
    this.input = form.querySelector('[data-search-input]');
    const scope = form.parentElement;
    this.results = scope.querySelector('[data-search-results]');
    this.fallback = scope.querySelector('[data-search-default]');
    this.timer = 0;
    this.abort = null;
    this.query = '';
    this.signal = header.abortController.signal;

    this.input?.addEventListener('input', this.#onInput, { signal: this.signal });
    this.input?.addEventListener('keydown', this.#onKeydown, { signal: this.signal });
    this.results?.addEventListener('keydown', this.#onResultsKeydown, { signal: this.signal });
    form.addEventListener('submit', this.#onSubmit, { signal: this.signal });
  }

  focus() {
    this.input?.focus({ preventScroll: true });
  }

  reset() {
    clearTimeout(this.timer);
    this.abort?.abort();
    this.query = '';
    if (this.input) this.input.value = '';
    this.#show(false);
  }

  #onSubmit = (event) => {
    if (!this.input || this.input.value.trim() === '') event.preventDefault();
  };

  #onInput = () => {
    clearTimeout(this.timer);
    const query = this.input.value.trim();
    if (query.length < SEARCH_MIN) {
      this.abort?.abort();
      this.query = '';
      this.#show(false);
      return;
    }
    this.timer = window.setTimeout(() => this.#fetch(query), SEARCH_DEBOUNCE);
  };

  #onKeydown = (event) => {
    if (event.key === 'ArrowDown' && this.results && !this.results.hidden) {
      const first = this.results.querySelector('[data-result]');
      if (first) {
        event.preventDefault();
        event.stopPropagation();
        first.focus();
      }
    }
  };

  #onResultsKeydown = (event) => {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    const items = [...this.results.querySelectorAll('[data-result]')];
    const index = items.indexOf(document.activeElement);
    if (index < 0) return;
    event.preventDefault();
    event.stopPropagation();
    if (event.key === 'ArrowUp' && index === 0) {
      this.focus();
      return;
    }
    const next = items[Math.min(items.length - 1, Math.max(0, index + (event.key === 'ArrowDown' ? 1 : -1)))];
    next?.focus();
  };

  async #fetch(query) {
    if (query === this.query) return;
    this.query = query;
    this.abort?.abort();
    this.abort = new AbortController();
    const base = this.header.dataset.suggestUrl || '/search/suggest';
    const url = new URL(base, window.location.origin);
    url.searchParams.set('q', query);
    url.searchParams.set('section_id', 'lucid-header-search');
    url.searchParams.set('resources[type]', 'product,collection,page');
    url.searchParams.set('resources[limit]', '6');
    url.searchParams.set('resources[limit_scope]', 'each');
    this.results?.setAttribute('aria-busy', 'true');
    try {
      const response = await fetch(url, { signal: this.abort.signal });
      if (!response.ok) throw new Error(String(response.status));
      const html = await response.text();
      if (query !== this.query) return;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const markup = doc.querySelector('[data-lucid-results]');
      if (!this.results) return;
      this.results.replaceChildren(...(markup ? [document.importNode(markup, true)] : []));
      this.#show(Boolean(markup));
    } catch (error) {
      if (error?.name !== 'AbortError') this.#show(false);
    } finally {
      this.results?.removeAttribute('aria-busy');
    }
  }

  #show(visible) {
    if (this.results) {
      this.results.hidden = !visible;
      if (!visible) this.results.replaceChildren();
    }
    if (this.fallback) this.fallback.hidden = visible;
    this.header.relayout();
  }
}

class LucidHeader extends HTMLElement {
  /** @type {AbortController} */
  abortController = new AbortController();
  #open = null;
  #sheetOpen = false;
  #openTimer = 0;
  #closeTimer = 0;
  #suppressFocus = false;
  #lastScrollTop = 0;
  #raf = 0;
  #offScroll = null;
  #resizeObserver = null;
  #groupObserver = null;
  #groupMutation = null;
  #lastTrigger = null;
  #searches = [];
  #mobile = window.matchMedia(MOBILE_QUERY);
  #compact = window.matchMedia(COMPACT_QUERY);
  #externalTrigger = null;
  #localeView = false;

  connectedCallback() {
    const signal = this.abortController.signal;
    this.frame = this.querySelector('[data-frame]');
    this.bar = this.querySelector('[data-bar]');
    this.nav = this.querySelector('[data-nav]');
    this.navLine = this.querySelector('[data-nav-line]');
    this.scrim = this.querySelector('[data-scrim]');
    this.sheetShadow = this.querySelector('[data-sheet-shadow]');
    this.sheet = this.querySelector('[data-sheet]');
    this.burger = this.querySelector('[data-burger]');
    this.sheetSearchButton = this.querySelector('[data-sheet-search]');
    this.triggers = [...this.querySelectorAll('[data-bar] [data-panel]')];
    this.navItems = [...this.querySelectorAll('[data-bar] .lucid-capsule__nav-item')];
    this.panels = new Map();
    for (const panel of this.querySelectorAll('[data-panel-key]')) {
      panel.style.setProperty('--pw', `${panel.dataset.w}px`);
      this.panels.set(panel.dataset.panelKey, panel);
    }
    this.order = [...this.panels.keys()];
    this.#searches = [...this.querySelectorAll('[data-search-form]')].map((form) => new HeaderSearch(form, this));
    this.locale = /** @type {any} */ (this.querySelector('[data-locale-root]'));
    this.localeEntry = this.querySelector('[data-locale-entry]');
    this.localeView = this.querySelector('[data-locale-view]');

    this.#bindDesktop(signal);
    this.#bindMobile(signal);
    this.#bindLocale(signal);
    this.#bindEditor(signal);

    this.#offScroll = onPageScroll(this.#onScroll);
    window.addEventListener('resize', this.relayout, { signal });
    this.#mobile.addEventListener('change', this.#onBreakpoint, { signal });
    document.fonts?.ready.then(() => this.relayout());

    this.#observeHeights();

    const layers = this.querySelectorAll('.lucid-capsule__edge, .lucid-capsule__glass');
    for (const layer of layers) layer.style.transition = 'none';
    this.#update();
    this.relayout();
    requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        for (const layer of layers) layer.style.transition = '';
      })
    );
  }

  disconnectedCallback() {
    this.abortController.abort();
    this.abortController = new AbortController();
    this.#offScroll?.();
    this.#resizeObserver?.disconnect();
    this.#groupObserver?.disconnect();
    this.#groupMutation?.disconnect();
    clearTimeout(this.#openTimer);
    clearTimeout(this.#closeTimer);
    cancelAnimationFrame(this.#raf);
    unlockScroll(this);
    document.documentElement.style.removeProperty('--lc-header-offset');
  }

  get isDark() {
    return this.hasAttribute('transparent');
  }

  get isMobile() {
    return this.#mobile.matches;
  }

  /** `scroll-up` hides on scroll down at every width, `scroll-up-mobile` only below the desktop layout. */
  get hidesOnScroll() {
    const mode = this.dataset.stickyMode;
    return mode === 'scroll-up' || (mode === 'scroll-up-mobile' && this.#compact.matches);
  }

  /* Scroll, tone and sticky state */

  #onScroll = () => {
    if (this.#raf) return;
    this.#raf = requestAnimationFrame(() => {
      this.#raf = 0;
      this.#update();
    });
  };

  #update() {
    const top = Math.max(0, pageScrollTop());
    const delta = top - this.#lastScrollTop;
    const scrolled = top > TONE_AFTER;
    this.dataset.scrolled = String(scrolled);
    this.dataset.tone = this.isDark && (!scrolled || this.hasAttribute('data-tone-locked')) ? 'dark' : 'light';

    let hidden = this.dataset.hidden === 'true';
    if (!this.hidesOnScroll || this.#open || this.#sheetOpen || !scrolled) {
      hidden = false;
    } else if (Math.abs(delta) > HIDE_THRESHOLD) {
      hidden = delta > 0 && top > this.offsetHeight;
    }
    if (this.querySelector(':focus-visible')) hidden = false;
    this.dataset.hidden = String(hidden);
    this.dataset.stickyState = hidden ? 'idle' : scrolled ? 'active' : 'inactive';
    if (Math.abs(delta) > HIDE_THRESHOLD || !scrolled) this.#lastScrollTop = top;

    const gap = parseFloat(getComputedStyle(this).getPropertyValue('--lc-cap-gap')) || 0;
    const bar = this.bar?.offsetHeight || 0;
    document.documentElement.style.setProperty('--lc-header-offset', `${hidden ? 0 : Math.round(gap + bar)}px`);
  }

  #observeHeights() {
    this.#resizeObserver = new ResizeObserver(([entry]) => {
      const size = entry?.borderBoxSize?.[0];
      if (size) document.body.style.setProperty('--header-height', `${Math.round(size.blockSize)}px`);
    });
    this.#resizeObserver.observe(this);

    const group = document.getElementById('header-group');
    if (!group) return;
    this.#groupObserver = new ResizeObserver(() => {
      let total = 0;
      for (const child of group.children) {
        if (child instanceof HTMLElement) total += child.offsetHeight;
      }
      document.body.style.setProperty('--header-group-height', `${Math.round(total)}px`);
      this.relayout();
    });
    const observe = () => {
      for (const child of group.children) {
        if (child instanceof HTMLElement) this.#groupObserver?.observe(child);
      }
    };
    observe();
    this.#groupMutation = new MutationObserver(observe);
    this.#groupMutation.observe(group, { childList: true });
  }

  /* Geometry */

  #dims() {
    const rect = this.frame.getBoundingClientRect();
    return { sw: rect.width, maxH: rect.height, capW: this.bar.getBoundingClientRect().width, bar: this.bar.offsetHeight };
  }

  #shape(w, h, r) {
    const { sw, maxH } = this.#dims();
    const side = Math.max(0, (sw - w) / 2);
    this.style.setProperty('--cl', `${side}px`);
    this.style.setProperty('--cr', `${side}px`);
    this.style.setProperty('--cb', `${Math.max(0, maxH - h)}px`);
    this.style.setProperty('--r', `${r}px`);
    if (this.sheetShadow) this.sheetShadow.style.transform = `scale(${w / sw}, ${Math.min(1, h / maxH)})`;
  }

  #closedShape() {
    for (const prop of ['--cl', '--cr', '--cb', '--r']) this.style.removeProperty(prop);
    if (!this.sheetShadow || !this.frame) return;
    const { sw, maxH, capW, bar } = this.#dims();
    if (this.isMobile) {
      this.sheetShadow.style.transform = '';
      return;
    }
    if (sw && maxH) this.sheetShadow.style.transform = `scale(${capW / sw}, ${bar / maxH})`;
  }

  /** A panel never gets narrower than the bar, so opening it cannot clip the bar's own items. */
  #panelSize(panel) {
    const { sw, bar, capW } = this.#dims();
    return { w: Math.max(capW, Math.min(Number(panel.dataset.w) || sw, sw)), h: bar + panel.offsetHeight };
  }

  #setViewportTop() {
    if (!this.frame) return;
    const top = this.getBoundingClientRect().top;
    const gap = parseFloat(getComputedStyle(this).getPropertyValue('--lc-cap-gap')) || 0;
    this.style.setProperty('--lc-cap-vtop', `${Math.max(0, Math.round(top + gap))}px`);
  }

  relayout = () => {
    if (!this.frame || !this.bar) return;
    if (!this.#open && !this.#sheetOpen) this.#setViewportTop();
    if (this.#open && !this.isMobile) {
      const panel = this.panels.get(this.#open);
      const size = this.#panelSize(panel);
      this.#shape(size.w, size.h, 20);
      this.#moveLine(this.#triggerFor(this.#open));
    } else if (this.#sheetOpen && this.isMobile) {
      this.style.setProperty('--cb', '0px');
      this.style.setProperty('--r', '20px');
    } else {
      this.#closedShape();
    }
  };

  #onBreakpoint = () => {
    this.closePanel();
    this.closeSheet();
    this.#placeLocale();
    this.relayout();
  };

  /* Desktop panels */

  #triggerFor(key) {
    return this.triggers.find((trigger) => trigger.dataset.panel === key) || null;
  }

  #moveLine(button) {
    if (!this.navLine) return;
    if (!button || !this.nav?.contains(button)) {
      this.navLine.style.setProperty('--lw', '0');
      return;
    }
    const navRect = this.nav.getBoundingClientRect();
    const rect = button.getBoundingClientRect();
    const pad = parseFloat(getComputedStyle(button).paddingLeft) || 0;
    this.navLine.style.setProperty('--lx', `${rect.left - navRect.left + pad}px`);
    this.navLine.style.setProperty('--lw', String(rect.width - 2 * pad));
  }

  prefetch(key) {
    loadImages(this.panels.get(key));
  }

  openPanel(key) {
    clearTimeout(this.#closeTimer);
    if (this.#open === key || this.isMobile) return;
    const next = this.panels.get(key);
    if (!next) return;
    loadImages(next);
    if (!this.#open) this.#setViewportTop();
    const previous = this.#open;
    const direction = previous ? Math.sign(this.order.indexOf(key) - this.order.indexOf(previous)) : 0;
    if (previous) {
      const old = this.panels.get(previous);
      old.classList.remove('is-on');
      old.classList.add(direction > 0 ? 'is-to-l' : 'is-to-r');
      old.inert = true;
      window.setTimeout(() => old.classList.remove('is-to-l', 'is-to-r'), 450);
    }
    next.classList.remove('is-to-l', 'is-to-r');
    if (direction) {
      next.classList.add(direction > 0 ? 'is-from-r' : 'is-from-l');
      void next.offsetWidth;
      next.classList.remove('is-from-r', 'is-from-l');
    }
    next.classList.add('is-on');
    next.inert = false;
    this.#open = key;
    for (const trigger of this.triggers) trigger.setAttribute('aria-expanded', String(trigger.dataset.panel === key));
    this.dataset.open = 'panel';
    const size = this.#panelSize(next);
    this.#shape(size.w, size.h, 20);
    this.#moveLine(this.#triggerFor(key));
    this.#update();
    if (key === 'search') {
      window.setTimeout(() => this.#searches[0]?.focus(), prefersReducedMotion() ? 0 : 200);
    }
    if (key === 'locale') this.#prepareLocale(next);
  }

  closePanel(focusBack = false) {
    clearTimeout(this.#openTimer);
    if (!this.#open) return;
    const panel = this.panels.get(this.#open);
    const trigger = this.#triggerFor(this.#open);
    panel?.classList.remove('is-on');
    if (panel) panel.inert = true;
    this.#open = null;
    for (const t of this.triggers) t.setAttribute('aria-expanded', 'false');
    delete this.dataset.open;
    this.#closedShape();
    this.#moveLine(null);
    this.#update();
    const external = this.#externalTrigger;
    this.#externalTrigger = null;
    if (focusBack && external?.isConnected) {
      external.focus({ preventScroll: true });
    } else if (focusBack && trigger) {
      this.#suppressFocus = true;
      trigger.focus();
      window.setTimeout(() => (this.#suppressFocus = false), 0);
    }
  }

  #bindDesktop(signal) {
    for (const trigger of this.triggers) {
      const key = trigger.dataset.panel;
      if (!CLICK_ONLY.has(key)) {
        trigger.addEventListener(
          'pointerenter',
          (event) => {
            if (event.pointerType === 'touch') return;
            this.prefetch(key);
            clearTimeout(this.#openTimer);
            clearTimeout(this.#closeTimer);
            this.#openTimer = window.setTimeout(() => this.openPanel(key), this.#open ? 0 : OPEN_DELAY);
          },
          { signal }
        );
        trigger.addEventListener('pointerleave', () => clearTimeout(this.#openTimer), { signal });
        trigger.addEventListener(
          'focus',
          () => {
            this.prefetch(key);
            if (!this.#suppressFocus && trigger.matches(':focus-visible')) this.openPanel(key);
          },
          { signal }
        );
      }
      trigger.addEventListener(
        'click',
        () => {
          if (this.#open === key && key !== 'search') this.closePanel(key === 'locale');
          else this.openPanel(key);
        },
        { signal }
      );
    }

    for (const link of this.querySelectorAll('[data-bar] a.lucid-capsule__nav-item')) {
      link.addEventListener(
        'pointerenter',
        () => {
          clearTimeout(this.#openTimer);
          if (this.#open && this.#open !== 'locale') {
            this.#closeTimer = window.setTimeout(() => this.closePanel(), LINK_CLOSE_DELAY);
          }
        },
        { signal }
      );
      link.addEventListener('focus', () => this.closePanel(), { signal });
    }

    this.frame?.addEventListener(
      'pointerleave',
      (event) => {
        if (event.pointerType === 'touch') return;
        clearTimeout(this.#openTimer);
        if (this.#open && !CLICK_ONLY.has(this.#open)) {
          this.#closeTimer = window.setTimeout(() => this.closePanel(), CLOSE_DELAY);
        }
      },
      { signal }
    );
    this.frame?.addEventListener('pointerenter', () => clearTimeout(this.#closeTimer), { signal });
    const warm = () => {
      if (this.isMobile) return;
      for (const panel of this.panels.values()) loadImages(panel);
    };
    this.bar?.addEventListener('pointerenter', warm, { signal, once: true });
    this.bar?.addEventListener('focusin', warm, { signal, once: true });
    this.scrim?.addEventListener(
      'click',
      () => {
        this.closePanel();
        this.closeSheet();
      },
      { signal }
    );
    this.addEventListener(
      'focusout',
      (event) => {
        if (this.isMobile) return;
        const next = /** @type {Node | null} */ (event.relatedTarget);
        if (this.#open === 'locale' && !next) return;
        if (!this.contains(next)) this.closePanel();
      },
      { signal }
    );
    this.addEventListener('keydown', this.#onDesktopKeydown, { signal });

    for (const panel of this.panels.values()) {
      for (const img of panel.querySelectorAll('img')) {
        img.addEventListener('load', () => this.#open && this.relayout(), { signal });
      }
    }
  }

  #onDesktopKeydown = (event) => {
    if (this.isMobile) return;
    const active = document.activeElement;
    const index = this.navItems.indexOf(active);

    if (event.key === 'Escape') {
      if (!this.#open) return;
      event.preventDefault();
      const wasSearch = this.#open === 'search';
      this.closePanel(true);
      if (wasSearch) this.#searches[0]?.reset();
      return;
    }

    if (this.#open === 'locale') {
      if (event.key !== 'Tab') return;
      const panel = this.panels.get('locale');
      const trigger = this.#triggerFor('locale');
      if (!panel) return;
      const stops = [...(trigger ? [trigger] : []), ...tabStops(panel)];
      if (!stops.length) return;
      let index = stops.indexOf(active);
      if (index < 0 && active instanceof HTMLInputElement && active.type === 'radio') {
        index = stops.findIndex((el) => el instanceof HTMLInputElement && el.name === active.name);
      }
      event.preventDefault();
      const step = event.shiftKey ? -1 : 1;
      const next = stops[(index + step + stops.length) % stops.length] || stops[0];
      next.focus();
      return;
    }

    if (index > -1 && (event.key === 'ArrowRight' || event.key === 'ArrowLeft')) {
      event.preventDefault();
      const visible = this.navItems.filter((item) => item.offsetParent);
      const position = visible.indexOf(active);
      visible[(position + (event.key === 'ArrowRight' ? 1 : -1) + visible.length) % visible.length]?.focus();
      return;
    }

    if (index > -1 && event.key === 'ArrowDown' && active.dataset.panel) {
      event.preventDefault();
      this.openPanel(active.dataset.panel);
      window.setTimeout(() => this.panels.get(this.#open)?.querySelector('a, input, button')?.focus(), 30);
      return;
    }

    const panel = this.#open ? this.panels.get(this.#open) : null;
    if (panel && panel.contains(active) && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
      if (active instanceof HTMLInputElement && event.key === 'ArrowDown') return;
      event.preventDefault();
      const items = [...panel.querySelectorAll('a, input:not([type="hidden"])')].filter(
        (el) => !el.closest('[hidden]')
      );
      const position = items.indexOf(active);
      if (event.key === 'ArrowUp' && position === 0) {
        this.#triggerFor(this.#open)?.focus();
        return;
      }
      items[Math.min(items.length - 1, Math.max(0, position + (event.key === 'ArrowDown' ? 1 : -1)))]?.focus();
      return;
    }

    if (event.key === 'Tab' && !event.shiftKey && active?.getAttribute?.('aria-expanded') === 'true' && panel) {
      const first = panel.querySelector('a, input:not([type="hidden"])');
      if (first) {
        event.preventDefault();
        first.focus();
      }
    }
  };

  /* Mobile sheet */

  openSheet(focusSearch = false, trigger = this.burger) {
    this.#lastTrigger = trigger || this.burger;
    loadImages(this.sheet);
    if (!this.#sheetOpen) {
      this.#setViewportTop();
      this.#sheetOpen = true;
      this.dataset.open = 'sheet';
      if (this.sheet) {
        this.sheet.inert = false;
        this.sheet.scrollTop = 0;
      }
      lockScroll(this);
      this.burger?.setAttribute('aria-expanded', 'true');
      if (this.burger?.dataset.labelClose) this.burger.setAttribute('aria-label', this.burger.dataset.labelClose);
      requestAnimationFrame(() => this.relayout());
      this.#update();
    }
    if (focusSearch) {
      const input = this.sheet?.querySelector('[data-sheet-input]');
      window.setTimeout(() => input?.focus({ preventScroll: true }), prefersReducedMotion() ? 0 : 360);
    }
  }

  closeSheet() {
    if (!this.#sheetOpen) return;
    this.#closeLocaleView(false);
    this.#sheetOpen = false;
    delete this.dataset.open;
    if (this.sheet) this.sheet.inert = true;
    unlockScroll(this);
    this.burger?.setAttribute('aria-expanded', 'false');
    if (this.burger?.dataset.labelOpen) this.burger.setAttribute('aria-label', this.burger.dataset.labelOpen);
    requestAnimationFrame(() => this.relayout());
    if (this.contains(document.activeElement) || this.#externalTrigger) {
      const target = this.#externalTrigger?.isConnected ? this.#externalTrigger : this.#lastTrigger;
      target?.focus({ preventScroll: true });
    }
    this.#externalTrigger = null;
    this.#update();
  }

  #bindMobile(signal) {
    const warm = () => loadImages(this.sheet);
    this.burger?.addEventListener('pointerdown', warm, { signal, once: true });
    this.burger?.addEventListener(
      'click',
      () => (this.#sheetOpen ? this.closeSheet() : this.openSheet(false, this.burger)),
      { signal }
    );
    this.sheetSearchButton?.addEventListener('click', () => this.openSheet(true, this.sheetSearchButton), { signal });

    this.addEventListener(
      'keydown',
      (event) => {
        if (!this.#sheetOpen) return;
        if (event.key === 'Escape') {
          event.preventDefault();
          this.closeSheet();
          return;
        }
        if (event.key !== 'Tab') return;
        const items = [...this.bar.querySelectorAll(FOCUSABLE), ...this.sheet.querySelectorAll(FOCUSABLE)].filter(
          (el) => el.offsetParent !== null && !el.closest('[hidden]')
        );
        const first = items[0];
        const last = items[items.length - 1];
        if (!first) return;
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      },
      { signal }
    );

    for (const button of this.querySelectorAll('[data-accordion]')) {
      button.addEventListener('click', () => this.#toggleAccordion(button), { signal });
    }

    for (const link of this.sheet?.querySelectorAll('a[href]') ?? []) {
      link.addEventListener(
        'click',
        () => {
          if (link.getAttribute('href')?.startsWith('#')) this.closeSheet();
        },
        { signal }
      );
    }

  }

  #toggleAccordion(button, force) {
    const panel = document.getElementById(button.getAttribute('aria-controls') || '');
    if (!panel) return;
    const expanded = button.getAttribute('aria-expanded') === 'true';
    const open = force ?? !expanded;
    for (const other of this.querySelectorAll('[data-accordion][aria-expanded="true"]')) {
      if (other === button) continue;
      other.setAttribute('aria-expanded', 'false');
      const otherPanel = document.getElementById(other.getAttribute('aria-controls') || '');
      if (otherPanel) otherPanel.hidden = true;
    }
    button.setAttribute('aria-expanded', String(open));
    panel.hidden = !open;
  }

  /* Country, currency and language */

  #bindLocale(signal) {
    if (!this.locale) return;
    this.#placeLocale();
    this.localeEntry?.addEventListener('click', () => this.#openLocaleView(), { signal });
    this.querySelector('[data-locale-back]')?.addEventListener('click', () => this.#closeLocaleView(true), { signal });
    this.locale.addEventListener(
      'lucid:locale:close',
      () => {
        if (this.isMobile) this.closeSheet();
        else this.closePanel(true);
      },
      { signal }
    );
    document.addEventListener(
      'lucid:locale:open',
      (event) => {
        const detail = /** @type {CustomEvent} */ (event).detail || {};
        event.preventDefault();
        this.#externalTrigger = detail.trigger || null;
        if (this.isMobile) {
          this.openSheet(false, this.burger);
          this.#externalTrigger = detail.trigger || null;
          this.#openLocaleView();
        } else {
          this.openPanel('locale');
        }
      },
      { signal }
    );
    const warm = () => {
      if (typeof this.locale?.load === 'function') this.locale.load();
    };
    this.#triggerFor('locale')?.addEventListener('pointerenter', warm, { signal, once: true });
    this.burger?.addEventListener('pointerdown', warm, { signal, once: true });
  }

  /** Keeps the single picker inside the host of the current layout. */
  #placeLocale() {
    if (!this.locale) return;
    const host = this.querySelector(`[data-locale-host="${this.isMobile ? 'sheet' : 'panel'}"]`);
    if (host && this.locale.parentElement !== host) host.append(this.locale);
  }

  async #prepareLocale(panel) {
    this.#placeLocale();
    const ready = await customElements.whenDefined('lucid-locale').then(() => this.locale.load());
    if (this.#open !== 'locale') return;
    this.locale.reset?.();
    this.relayout();
    if (!ready) return;
    window.setTimeout(
      () => {
        if (this.#open === 'locale') this.locale.focusStart?.({ preferSearch: true });
      },
      prefersReducedMotion() ? 0 : 180
    );
    for (const img of panel.querySelectorAll('img')) {
      img.addEventListener('load', () => this.#open === 'locale' && this.relayout(), { once: true });
    }
  }

  async #openLocaleView() {
    if (!this.localeView || !this.sheet) return;
    this.#placeLocale();
    this.#localeView = true;
    this.sheet.dataset.view = 'locale';
    this.localeView.hidden = false;
    this.localeEntry?.setAttribute('aria-expanded', 'true');
    this.sheet.scrollTop = 0;
    const ready = await customElements.whenDefined('lucid-locale').then(() => this.locale.load());
    if (!this.#localeView) return;
    this.locale.reset?.();
    const focus = () => {
      if (!this.#localeView) return;
      const title = this.locale.querySelector('.lucid-locale__title');
      if (ready && title instanceof HTMLElement) title.focus({ preventScroll: true });
      else this.localeView?.querySelector('[data-locale-back]')?.focus({ preventScroll: true });
    };
    window.setTimeout(focus, prefersReducedMotion() ? 0 : 200);
  }

  #closeLocaleView(focusEntry) {
    if (!this.#localeView || !this.sheet || !this.localeView) return;
    this.#localeView = false;
    delete this.sheet.dataset.view;
    this.localeView.hidden = true;
    this.localeEntry?.setAttribute('aria-expanded', 'false');
    this.sheet.scrollTop = 0;
    if (focusEntry) this.localeEntry?.focus({ preventScroll: true });
  }

  /* Theme editor */

  #bindEditor(signal) {
    if (!window.Shopify?.designMode) return;
    document.addEventListener(
      'shopify:block:select',
      (event) => {
        const target = /** @type {HTMLElement} */ (event.target);
        if (!this.contains(target)) return;
        if (target.dataset.panelKey) {
          if (this.isMobile) {
            this.openSheet(false);
            const index = target.dataset.panelKey.replace('p', '');
            const button = this.querySelector(`[aria-controls$="-${CSS.escape(index)}"][data-accordion]`);
            if (button) this.#toggleAccordion(button, true);
          } else {
            this.openPanel(target.dataset.panelKey);
          }
        } else if (this.sheet?.contains(target)) {
          this.openSheet(false);
          loadImages(this.sheet);
        }
      },
      { signal }
    );
    document.addEventListener(
      'shopify:block:deselect',
      (event) => {
        if (!this.contains(/** @type {Node} */ (event.target))) return;
        this.closePanel();
        this.closeSheet();
      },
      { signal }
    );
    document.addEventListener(
      'shopify:section:deselect',
      (event) => {
        if (!(/** @type {HTMLElement} */ (event.target)).contains(this)) return;
        this.closePanel();
        this.closeSheet();
      },
      { signal }
    );
  }
}

/** Ice notification strip: rotating messages, pause on hover and focus, dismiss for the session. */
class LucidHeaderStrip extends HTMLElement {
  #timer = 0;
  #index = 0;
  #paused = false;
  #abort = new AbortController();

  connectedCallback() {
    const signal = this.#abort.signal;
    this.messages = [...this.querySelectorAll('[data-strip-msg]')];
    this.#index = Math.max(0, this.messages.findIndex((msg) => msg.classList.contains('is-on')));
    const pause = () => (this.#paused = true);
    const resume = () => (this.#paused = false);
    this.addEventListener('pointerenter', pause, { signal });
    this.addEventListener('pointerleave', resume, { signal });
    this.addEventListener('focusin', pause, { signal });
    this.addEventListener('focusout', resume, { signal });
    this.querySelector('[data-strip-close]')?.addEventListener('click', this.#dismiss, { signal });

    if (window.Shopify?.designMode) {
      document.addEventListener(
        'shopify:block:select',
        (event) => {
          const index = this.messages.indexOf(/** @type {HTMLElement} */ (event.target));
          if (index > -1) {
            this.#paused = true;
            this.#show(index);
          }
        },
        { signal }
      );
      document.addEventListener('shopify:block:deselect', resume, { signal });
    }

    if (this.messages.length > 1 && !prefersReducedMotion()) {
      const speed = Math.max(3, Number(this.dataset.speed) || 5) * 1000;
      this.#timer = window.setInterval(() => {
        if (this.#paused || document.hidden) return;
        this.#show((this.#index + 1) % this.messages.length);
      }, speed);
    }
  }

  disconnectedCallback() {
    clearInterval(this.#timer);
    this.#abort.abort();
    this.#abort = new AbortController();
  }

  #show(index) {
    if (index === this.#index) return;
    const current = this.messages[this.#index];
    const next = this.messages[index];
    if (!next) return;
    if (current) {
      current.classList.remove('is-on');
      current.classList.add('is-out');
      current.setAttribute('aria-hidden', 'true');
      current.inert = true;
      window.setTimeout(() => current.classList.remove('is-out'), 700);
    }
    next.classList.remove('is-out');
    next.classList.add('is-on');
    next.removeAttribute('aria-hidden');
    next.inert = false;
    this.#index = index;
  }

  #dismiss = () => {
    if (this.dataset.dismissKey) session.set(this.dataset.dismissKey, '1');
    const section = this.closest('.shopify-section');
    if (section instanceof HTMLElement) section.hidden = true;
    else this.hidden = true;
    document.querySelector('lucid-header')?.relayout?.();
  };
}

if (!customElements.get('lucid-header')) customElements.define('lucid-header', LucidHeader);
if (!customElements.get('lucid-header-strip')) customElements.define('lucid-header-strip', LucidHeaderStrip);
